import * as React from 'react';
import { Button, Dropdown, Option, Switch, Textarea, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { DeleteRegular, ArrowCounterclockwiseRegular, PlugDisconnectedRegular, SignOutRegular } from '@fluentui/react-icons';
import { PROGRAMS, STAGES } from '../../api/src/programs';
import { ProgramsStep } from './Onboarding';
import { DEFAULT_UPLOAD_INTRO } from '../records/uploadEmail';
import type { AccountInfo } from '@azure/msal-browser';
import type { IGraphClient } from '../../../src/services/GraphService';
import { errorText, useApp } from '../app/context';
import { deleteOrganisationData, forgetLibrary } from '../api';
import { signOut } from '../auth';
import { isPaused } from '../../api/src/workspace';
import BackgroundCard from '../components/BackgroundCard';
import FirmSettings from '../components/FirmSettings';
import UploadsCard from '../components/UploadsCard';
import RestrictedCard from '../components/RestrictedCard';
import { PRODUCT_NAME, UPGRADE_MAIL } from '../app/product';

type Confirm = 'redo' | 'disconnect' | 'delete' | undefined;

/** Forgets this browser's saved onboarding progress for a library. */
function clearOnboardingDraft(driveId: string): void {
  try {
    Object.keys(window.localStorage).filter(k => k.startsWith('aiadmin.onboarding.') && k.endsWith(driveId)).forEach(k => window.localStorage.removeItem(k));
  } catch {
    // Nothing saved in this browser.
  }
}

/**
 * The person's account and this library: who's signed in, the plan, starting setup again,
 * disconnecting the library, and deleting what AI Admin keeps. Nothing here ever deletes files.
 */
export default function Settings(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const org = useOrganisationName(app.graph);
  const [confirm, setConfirm] = React.useState<Confirm>(undefined);
  const [typed, setTyped] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [open, setOpen] = React.useState<string | undefined>(undefined);
  const backgroundOn: boolean = app.workspaces.length > 0 && w.background?.enabled === true;

  const close = (): void => { setConfirm(undefined); setTyped(''); };

  const redoSetup = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      clearOnboardingDraft(w.driveId);
      await app.saveWorkspace({ ...w, setupDone: false });
      close();
      app.go('setup');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      clearOnboardingDraft(w.driveId);
      await forgetLibrary(w.driveId);
      window.location.hash = '/home';
      window.location.reload();
    } catch (e) {
      setError(errorText(e));
      setWorking(false);
    }
  };

  const deleteEverything = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      await deleteOrganisationData();
      app.workspaces.forEach(x => clearOnboardingDraft(x.driveId));
      window.location.hash = '/home';
      window.location.reload();
    } catch (e) {
      setError(errorText(e));
      setWorking(false);
    }
  };

  const confirmWord: string = org ?? 'delete';

  const toggleFiling = async (on: boolean): Promise<void> => {
    setError(undefined);
    try {
      await app.saveWorkspace({ ...w, paused: !on, pausedUntil: undefined });
      app.toast({ text: on ? 'Inbox filing is on.' : 'Inbox filing is off. New files wait in Inbox.' });
    } catch (e) {
      setError(errorText(e));
    }
  };

  const firm = w.firm;
  const staff = firm?.staff ?? [];
  const programCount: number = firm ? (firm.programs.length || PROGRAMS.length) : 0;
  const pending: boolean = PROGRAMS.some(p => p.review === 'pending' && (!firm?.programs.length || firm.programs.indexOf(p.id) !== -1));
  const toggle = (k: string): void => setOpen(o => (o === k ? undefined : k));
  const row = (k: string, title: string, value: React.ReactNode, action: string | undefined, body?: React.ReactNode, onAction?: () => void): React.ReactElement => (
    <>
      <div className="set">
        <div className="stack"><strong>{title}</strong><span className="val">{value}</span></div>
        {action && <Button aria-expanded={body ? open === k : undefined} onClick={onAction ?? (() => toggle(k))}>{body && open === k ? 'Close' : action}</Button>}
      </div>
      {body && open === k && <div className="set-open">{body}</div>}
    </>
  );

  return (
    <main className="page" style={{ maxWidth: 908 }}>
      <h1 className="h1">Settings</h1>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}

      {firm && (
        <section className="card" aria-labelledby="s-firm" style={{ gap: 0 }}>
          <h2 className="h" id="s-firm">Your firm</h2>
          {row('team', 'Team', staff.length ? staff.map(x => `${x.name} (${x.role})`).join(', ') : 'Nobody yet', 'Edit', <FirmSettings part="team" />)}
          {row('programs', 'Programs', `${programCount} of ${PROGRAMS.length}${pending ? ' · checklists pending lawyer review' : ''}`, 'Edit', <ProgramsEditor />)}
          {row('instalments', 'Fee instalment reminders', firm.instalments?.length ? firm.instalments.map(i => `${i.label}, at ${STAGES.filter(st => st.id === i.stage)[0]?.label.toLowerCase() ?? i.stage}`).join(' · ') : 'None', 'Edit', <FirmSettings part="instalments" />)}
        </section>
      )}

      <section className="card" aria-labelledby="s-feat" style={{ gap: 0 }}>
        <h2 className="h" id="s-feat">Optional features</h2>
        {firm && row('uploads', 'Client upload links',
          w.uploads?.enabled ? 'On. Email a client a link from any application; their files go straight into its folder.' : 'Off. Clients send documents through a link that expires, straight into your SharePoint.',
          w.uploads?.enabled ? 'Change' : 'Turn on',
          <><UploadsCard />{w.uploads?.enabled && <UploadIntroEditor />}</>)}
        {firm?.listsReady && row('restricted', 'Restricted files',
          firm.restricted ? `Set up. Refugee and private-sponsorship files are in "${firm.restricted.libraryName}", open only to its group.` : 'Not set up. Refugee and private-sponsorship files go in their own library, open only to a group you choose. A site owner sets this up.',
          firm.restricted ? 'Manage' : 'Set up', <RestrictedCard />)}
        {row('outlook', 'Outlook drafts and calendar', 'Drafts and calendar events are made in your own Outlook when you ask. Nothing is ever sent for you.', undefined)}
        {row('inbox', 'Inbox filing', isPaused(w) ? `Off. When on, documents dropped into ${w.inboxName} are named and filed for you.` : `On. Documents dropped into ${w.inboxName} are named and filed for you.`,
          isPaused(w) ? 'Turn on' : 'Change',
          <>
            <Switch label={isPaused(w) ? 'Off' : 'On'} checked={!isPaused(w)} onChange={(_, d) => { void toggleFiling(d.checked); }} />
            {!isPaused(w) && <BackgroundCard />}
          </>)}
      </section>

      <section className="card" aria-labelledby="s-plan" style={{ gap: 0 }}>
        <h2 className="h" id="s-plan">Plan</h2>
        {row('plan', app.plan === 'paid' ? 'Paid' : 'Free trial',
          app.plan === 'paid' ? 'Billed per application opened.' : app.trial ? `${app.trial.applications} free application${app.trial.applications === 1 ? '' : 's'} left this year` : 'Your first 5 applications are free.',
          app.plan === 'paid' ? undefined : 'See plans', undefined, () => { window.location.href = UPGRADE_MAIL; })}
      </section>

      <section className="card" aria-labelledby="s-acc" style={{ gap: 0 }}>
        <h2 className="h" id="s-acc">Your account</h2>
        {row('me', app.account.name || app.account.username, `${app.account.username}${org ? ` · ${org}` : ''}`, 'Sign out', undefined, () => { void signOut(); })}
      </section>

      <details className="card">
        <summary style={{ cursor: 'pointer', fontWeight: 600, minHeight: 32, display: 'flex', alignItems: 'center' }}>Site and setup</summary>
        <div className="stack" style={{ gap: 12, paddingTop: 8 }}>
          <span className="val muted">{w.siteName ? `${w.siteName} · ` : ''}{w.libraryName} library</span>
          {app.workspaces.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14 }}>
              Library:
              <Dropdown aria-label="Library" value={`${w.libraryName} · ${w.siteName}`} selectedOptions={[w.driveId]} onOptionSelect={(_, d) => d.optionValue && app.switchLibrary(d.optionValue)} style={{ minWidth: 240 }}>
                {app.workspaces.map(x => <Option key={x.driveId} value={x.driveId}>{`${x.libraryName} · ${x.siteName}`}</Option>)}
              </Dropdown>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button icon={<ArrowCounterclockwiseRegular />} onClick={() => setConfirm('redo')}>Run setup again</Button>
            <Button onClick={() => app.go('connect')}>Connect another library</Button>
            <Button icon={<PlugDisconnectedRegular />} onClick={() => setConfirm('disconnect')}>Disconnect this library</Button>
          </div>
          <span className="small">Running setup again or disconnecting leaves all your records and documents in SharePoint.</span>
          <div className="stack" style={{ gap: 8, paddingTop: 12, borderTop: '1px solid var(--line-2)' }}>
            <strong>Delete everything {PRODUCT_NAME} keeps</strong>
            <span className="small" style={{ fontSize: 14 }}>
              Deletes the settings, review queues and saved suggestions for every library in your organisation.
              Your documents, folders, records lists and History in SharePoint are not touched. Billing records (plan and usage counts) are kept, as our privacy page explains.
            </span>
            <Button icon={<DeleteRegular />} onClick={() => setConfirm('delete')} style={{ alignSelf: 'flex-start', color: 'var(--red)' }}>Delete everything</Button>
          </div>
        </div>
      </details>

      <Dialog open={!!confirm} onOpenChange={(_, d) => { if (!d.open && !working) { close(); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {confirm === 'redo' ? 'Start setup again?' : confirm === 'disconnect' ? `Disconnect ${w.libraryName}?` : `Delete everything ${PRODUCT_NAME} keeps?`}
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {confirm === 'redo' && <span>You'll go through setup again. Until you finish it, new documents wait in the Inbox. Nothing in your library changes.</span>}
              {confirm !== 'redo' && backgroundOn && (
                <MessageBar intent="warning"><MessageBarBody>
                  Filing in the background is on. Turn it off first under Inbox filing on this page, so {PRODUCT_NAME}'s access to the site is removed too.
                </MessageBarBody></MessageBar>
              )}
              {confirm === 'disconnect' && <span>{PRODUCT_NAME} forgets this library's settings and review queue. You can connect it again later and set it up from the start. Your files stay where they are.</span>}
              {confirm === 'delete' && (
                <>
                  <span>This can't be undone. Every library will need to be set up again. Your documents in SharePoint are not touched.</span>
                  <span>Type <strong>{confirmWord}</strong> to confirm.</span>
                  <Input aria-label="Type to confirm" value={typed} onChange={(_, d) => setTyped(d.value)} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button disabled={working} onClick={close}>Cancel</Button>
              <Button appearance="primary" disabled={working || (confirm !== 'redo' && backgroundOn) || (confirm === 'delete' && typed.trim().toLowerCase() !== confirmWord.toLowerCase())}
                onClick={() => { void (confirm === 'redo' ? redoSetup() : confirm === 'disconnect' ? disconnect() : deleteEverything()); }}>
                {working ? 'Working…' : confirm === 'redo' ? 'Start setup again' : confirm === 'disconnect' ? 'Disconnect' : 'Delete everything'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}

/** The organisation's name from Microsoft (User.Read is enough), or undefined. */
function useOrganisationName(graph: IGraphClient): string | undefined {
  const [org, setOrg] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    graph.api('/organization?$select=displayName').get()
      .then((r: { value: { displayName: string }[] }) => setOrg(r.value[0]?.displayName))
      .catch(() => setOrg(undefined));
  }, [graph]);
  return org;
}

/**
 * The Account page before any library is connected (the profile menu works everywhere): who's
 * signed in, sign out, and deleting what AI Admin keeps about the organisation.
 */
export function AccountOnly(props: { account: AccountInfo; graph: IGraphClient; libraries: { driveId: string }[] }): React.ReactElement {
  const org = useOrganisationName(props.graph);
  const [open, setOpen] = React.useState<boolean>(false);
  const [typed, setTyped] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const confirmWord: string = org ?? 'delete';
  const deleteEverything = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      await deleteOrganisationData();
      props.libraries.forEach(x => clearOnboardingDraft(x.driveId));
      window.location.hash = '/home';
      window.location.reload();
    } catch (e) {
      setError(errorText(e));
      setWorking(false);
    }
  };
  return (
    <main className="page" style={{ maxWidth: 880, margin: '0 auto', width: '100%' }}>
      <div className="page-head">
        <div>
          <h1>Account</h1>
          <p>Who's signed in, and what {PRODUCT_NAME} keeps. <a href="#/home">Back</a></p>
        </div>
      </div>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <section className="card" style={{ gap: 10 }}>
        <h2>You</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', gap: '8px 16px', fontSize: 15 }}>
          <span className="muted">Name</span><span>{props.account.name || '—'}</span>
          <span className="muted">Signed in as</span><span>{props.account.username}</span>
          <span className="muted">Organisation</span><span>{org ?? '—'}</span>
        </div>
        <Button icon={<SignOutRegular />} onClick={() => { void signOut(); }} style={{ alignSelf: 'flex-start' }}>Sign out</Button>
      </section>
      {props.libraries.length > 0 && (
        <section className="card" style={{ gap: 10, borderColor: 'var(--red)' }}>
          <h2>Delete everything {PRODUCT_NAME} keeps</h2>
          <span className="small" style={{ fontSize: 14 }}>Deletes the settings, client and matter lists and review queues for every library in your organisation. Your documents in SharePoint are not touched. Billing records are kept.</span>
          {!open && <Button appearance="primary" icon={<DeleteRegular />} onClick={() => setOpen(true)} style={{ alignSelf: 'flex-start', background: 'var(--red)' }}>Delete everything</Button>}
          {open && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
              <span>This can't be undone. Type <strong>{confirmWord}</strong> to confirm.</span>
              <Input aria-label="Type to confirm" value={typed} onChange={(_, d) => setTyped(d.value)} />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button appearance="primary" disabled={working || typed.trim().toLowerCase() !== confirmWord.toLowerCase()} onClick={() => { void deleteEverything(); }}>{working ? 'Deleting…' : 'Delete everything'}</Button>
                <Button disabled={working} onClick={() => { setOpen(false); setTyped(''); }}>Cancel</Button>
              </div>
            </div>
          )}
        </section>
      )}
    </main>
  );
}

/** The programs the firm offers (all when none are chosen), changed after setup. */
function ProgramsEditor(): React.ReactElement | null {
  const app = useApp();
  const w = app.workspace;
  const [programs, setPrograms] = React.useState<string[]>(() => (w.firm?.programs.length ? w.firm.programs : PROGRAMS.map(p => p.id)));
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  if (!w.firm) {
    return null;
  }
  const save = async (): Promise<void> => {
    setSaving(true);
    setError(undefined);
    try {
      await app.saveWorkspace({ ...w, firm: { ...w.firm!, programs: programs.length === PROGRAMS.length ? [] : programs } });
      app.toast({ text: 'Saved. Open applications keep the checklist they were opened with.' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <ProgramsStep programs={programs} onChange={setPrograms} />
      <Button appearance="primary" disabled={saving || !programs.length} onClick={() => { void save(); }} style={{ alignSelf: 'flex-start' }}>{saving ? 'Saving…' : 'Save'}</Button>
    </>
  );
}

/** The opening of the "upload link" email, as the firm wants it to read. */
function UploadIntroEditor(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const [text, setText] = React.useState<string>(w.uploadIntro ?? DEFAULT_UPLOAD_INTRO);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const save = async (value: string | undefined): Promise<void> => {
    setSaving(true);
    setError(undefined);
    try {
      await app.saveWorkspace({ ...w, uploadIntro: value });
      setText(value ?? DEFAULT_UPLOAD_INTRO);
      app.toast({ text: 'Saved. New upload link emails open with this text.' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="stack" style={{ gap: 8, paddingTop: 8 }}>
      <label htmlFor="upload-intro" style={{ fontWeight: 600, fontSize: 14 }}>How the email to the client opens</label>
      <span className="small">The file number, client, program, documents still needed and the link follow it. Restricted files' emails have only the file number and the link.</span>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <Textarea id="upload-intro" resize="vertical" rows={5} maxLength={1500} value={text} onChange={(_, d) => setText(d.value)} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button appearance="primary" disabled={saving || text.trim() === (w.uploadIntro ?? DEFAULT_UPLOAD_INTRO).trim()} onClick={() => { void save(text.trim() === DEFAULT_UPLOAD_INTRO ? undefined : text); }}>{saving ? 'Saving…' : 'Save'}</Button>
        {w.uploadIntro && <Button disabled={saving} onClick={() => { void save(undefined); }}>Use the standard text</Button>}
      </div>
    </div>
  );
}
